import React, { useMemo, useEffect, useRef, useState } from "react";
import { computeSectionBreakdown } from "./SOQRegisterSection";
import { computeFinanceSummary } from "../utils/financeSummary";
import { tokens as T } from "./theme";

// ─── Utilities ────────────────────────────────────────────────────────────────
// Strips currency symbols / commas before parsing so "R 1,234.56" → 1234.56
function numberOrZero(value) {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const cleaned = String(value).replace(/[^0-9.-]/g, "");
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
}
function fmt(value, compact = false) {
  const n = numberOrZero(value);
  if (compact) {
    if (Math.abs(n) >= 1_000_000) return `R ${(n / 1_000_000).toFixed(2)}m`;
    if (Math.abs(n) >= 1_000)     return `R ${(n / 1_000).toFixed(0)}k`;
    return `R ${n.toFixed(0)}`;
  }
  return `R ${n.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtPct(v) { return `${numberOrZero(v).toFixed(1)}%`; }
function isApprovedVariation(item) {
  return String(item?.status || "").trim().toLowerCase() === "approved";
}

// ─── Design tokens (sourced from the house design system) ───────────────────────
const C = {
  // surfaces
  canvas: T.color.canvas, surface: T.color.surface, surfaceAlt: T.color.surfaceAlt, sunken: T.color.surfaceSunken,
  border: T.color.border, borderStrong: T.color.borderStrong,
  // ink
  ink: T.color.ink, inkMuted: T.color.inkMuted, inkSubtle: T.color.inkSubtle, inkOnDark: T.color.inkOnDark,
  // brand
  navy: T.color.primary, navyHover: T.color.primaryHover, navySoft: T.color.primarySoft, navyMid: T.color.primaryAccent,
  dark: T.color.dark, darkAlt: T.color.darkAlt, darkBorder: T.color.darkBorder,
  accent: T.color.accent, accentHover: T.color.accentHover, accentSoft: T.color.accentSoft,
  // status
  green: T.color.success, greenSoft: T.color.successSoft, greenBorder: T.color.successBorder,
  amber: T.color.warning, amberSoft: T.color.warningSoft, amberBorder: T.color.warningBorder,
  red: T.color.danger, redSoft: T.color.dangerSoft, redBorder: T.color.dangerBorder,
  info: T.color.info, infoSoft: T.color.infoSoft, infoBorder: T.color.infoBorder,
};
const DISP = T.font.display;
const BODY = T.font.body;
const MONO = T.font.mono;
const SH = T.shadow;

const CSS = `
.fd-root *{box-sizing:border-box}
@keyframes fd-rise{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
@keyframes fd-grow{from{transform:scaleY(0)}to{transform:scaleY(1)}}
@keyframes fd-bar{from{width:0}to{width:var(--w)}}
.fd-root{font-family:${BODY};background:${C.canvas};color:${C.ink};min-height:100vh;padding:28px;-webkit-font-smoothing:antialiased}
.fd-root .mono{font-family:${MONO};font-variant-numeric:tabular-nums;font-feature-settings:"tnum" 1}
.fd-eyebrow{font-family:${DISP};font-size:10px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:${C.inkSubtle}}
.fd-card{background:${C.surface};border:1px solid ${C.border};border-radius:${T.radius.lg};box-shadow:${SH.sm};animation:fd-rise .4s cubic-bezier(.4,0,.2,1) both}
.fd-card-pad{padding:22px 24px}
.fd-sectitle{font-family:${DISP};font-size:13px;font-weight:700;letter-spacing:.01em;color:${C.ink};display:flex;align-items:center;gap:10px;margin:0}
.fd-sectitle .rule{flex:1;height:1px;background:${C.border}}
.fd-stat-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(186px,1fr));gap:14px;margin-bottom:18px}
.fd-stat{background:${C.surface};border:1px solid ${C.border};border-radius:${T.radius.md};padding:16px 18px;box-shadow:${SH.sm};position:relative;overflow:hidden;animation:fd-rise .4s cubic-bezier(.4,0,.2,1) both}
.fd-stat::before{content:"";position:absolute;left:0;top:0;bottom:0;width:3px;background:var(--accent,${C.navy})}
.fd-stat-label{font-family:${DISP};font-size:10px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:${C.inkSubtle};margin-bottom:7px}
.fd-stat-value{font-family:${MONO};font-size:22px;font-weight:600;letter-spacing:-.01em;font-variant-numeric:tabular-nums;line-height:1.1}
.fd-stat-sub{font-size:11px;color:${C.inkMuted};margin-top:6px;line-height:1.45}
.fd-chip{display:inline-flex;align-items:center;gap:5px;padding:3px 9px;border-radius:${T.radius.full};font-size:10px;font-weight:700;font-family:${DISP};letter-spacing:.04em;margin-top:8px;border:1px solid transparent}
.fd-chip-ok{background:${C.greenSoft};color:${C.green};border-color:${C.greenBorder}}
.fd-chip-warn{background:${C.amberSoft};color:${C.amber};border-color:${C.amberBorder}}
.fd-chip-over{background:${C.redSoft};color:${C.red};border-color:${C.redBorder}}
.fd-chip-info{background:${C.infoSoft};color:${C.info};border-color:${C.infoBorder}}
.fd-pill{display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:${T.radius.xs};font-size:10px;font-weight:700;font-family:${DISP};letter-spacing:.03em}
.fd-pill-over{background:${C.redSoft};color:${C.red}}
.fd-pill-ok{background:${C.greenSoft};color:${C.green}}
.fd-pill-warn{background:${C.amberSoft};color:${C.amber}}
.fd-pill-info{background:${C.navySoft};color:${C.navy}}
.fd-table{width:100%;border-collapse:collapse;font-size:12px}
.fd-table thead th{padding:9px 12px;text-align:left;font-family:${DISP};font-size:10px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:${C.inkSubtle};border-bottom:1.5px solid ${C.borderStrong};white-space:nowrap}
.fd-table thead th.r{text-align:right}
.fd-table tbody tr{border-bottom:1px solid ${C.border};transition:background ${T.motion.fast}}
.fd-table tbody tr:hover{background:${C.surfaceAlt}}
.fd-table tbody td{padding:9px 12px;color:${C.ink};vertical-align:middle}
.fd-table tbody td.r{text-align:right;font-family:${MONO};font-size:12px;font-variant-numeric:tabular-nums}
.fd-track{height:8px;background:${C.sunken};border-radius:${T.radius.full};overflow:hidden;border:1px solid ${C.border}}
.fd-fill{height:100%;border-radius:${T.radius.full};animation:fd-bar .9s cubic-bezier(.4,0,.2,1) both;animation-delay:.2s}
`;

// ─── Animated counter ──────────────────────────────────────────────────────────
function AnimCounter({ target, duration = 850 }) {
  const [v, setV] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    const t0 = performance.now();
    function tick(now) {
      const p = Math.min((now - t0) / duration, 1);
      setV(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf.current = requestAnimationFrame(tick);
    }
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration]);
  return <span>{fmt(v, true)}</span>;
}

// ─── HERO: Tender baseline vs current position ───────────────────────────────────
// Two stacked columns on a shared scale, with the tender envelope drawn as a
// reference line and the delta called out. Rendered on a deep-navy band so it
// reads as the executive headline of the dashboard.
function ComparisonColumns({ tenderTotal, tenderSegs, positionTotal, positionSegs, committedTotal }) {
  const W = 600, H = 320, PT = 34, PB = 58, PL = 70, PR = 26;
  const pw = W - PL - PR, ph = H - PT - PB;
  const maxV = Math.max(tenderTotal, positionTotal, 1) * 1.14;
  const toY = (v) => PT + ph - (v / maxV) * ph;
  const colW = 104, centerGap = 92;
  const x1 = PL + pw / 2 - centerGap / 2 - colW;
  const x2 = PL + pw / 2 + centerGap / 2;
  const grid = Array.from({ length: 5 }, (_, i) => { const v = (maxV / 4) * i; return { v, y: toY(v) }; });
  const refY = toY(tenderTotal);
  // The committed total (everything except pending exposure) gets a marker line
  // on the position column so the eye can separate committed from at-risk value.
  const committed = committedTotal == null ? positionTotal : committedTotal;

  function Stack({ x, segs }) {
    let acc = 0;
    return segs.map((s, i) => {
      const v = Math.max(0, s.value);
      const y0 = toY(acc), y1 = toY(acc + v);
      acc += v;
      const h = Math.max(0, y0 - y1);
      const showLabel = h > 22;
      const patId = `fd-hatch-${i}`;
      return (
        <g key={i} style={{ transformBox: "fill-box", transformOrigin: "bottom", animation: `fd-grow .7s cubic-bezier(.34,1.56,.64,1) both`, animationDelay: `${.15 + i * .08}s` }}>
          {s.pending && (
            <defs>
              <pattern id={patId} patternUnits="userSpaceOnUse" width="7" height="7" patternTransform="rotate(45)">
                <rect width="7" height="7" fill={s.color} fillOpacity={.55} />
                <line x1="0" y1="0" x2="0" y2="7" stroke="#fff" strokeOpacity={.45} strokeWidth="2" />
              </pattern>
            </defs>
          )}
          <rect x={x} y={y1} width={colW} height={h} fill={s.pending ? `url(#${patId})` : s.color} stroke={s.pending ? s.color : "none"} strokeWidth={s.pending ? 1 : 0} strokeDasharray={s.pending ? "3 2" : "0"} rx={2} />
          {showLabel && (
            <text x={x + colW / 2} y={(y0 + y1) / 2 + 4} textAnchor="middle" fontSize={11} fontFamily={MONO} fontWeight="600" fill={s.text || "#fff"}>{fmt(v, true)}</text>
          )}
        </g>
      );
    });
  }

  const delta = positionTotal - tenderTotal;
  const under = delta <= 0;
  const dCol = under ? C.green : C.accent;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }}>
      {/* gridlines */}
      {grid.map(({ v, y }, i) => (
        <g key={i}>
          <line x1={PL} y1={y} x2={W - PR} y2={y} stroke="#FFFFFF" strokeOpacity={i === 0 ? .28 : .08} strokeWidth={1} />
          <text x={PL - 8} y={y + 4} textAnchor="end" fontSize={9} fontFamily={MONO} fill="#9FB2C9">{fmt(v, true)}</text>
        </g>
      ))}
      {/* tender envelope reference line */}
      <line x1={PL} y1={refY} x2={W - PR} y2={refY} stroke={C.accent} strokeWidth={1.5} strokeDasharray="5 4" opacity={.9} />
      <text x={W - PR} y={refY - 6} textAnchor="end" fontSize={9.5} fontFamily={DISP} fontWeight="700" letterSpacing=".06em" fill={C.accent}>TENDER ENVELOPE</text>

      <Stack x={x1} segs={tenderSegs} />
      <Stack x={x2} segs={positionSegs} />

      {/* committed-vs-pending divider on the position column */}
      {committed < positionTotal && committed > 0 && (
        <g>
          <line x1={x2 - 6} y1={toY(committed)} x2={x2 + colW + 6} y2={toY(committed)} stroke="#fff" strokeWidth={1.5} strokeDasharray="4 3" opacity={.85} />
          <text x={x2 + colW + 10} y={toY(committed) + 3} fontSize={8.5} fontFamily={DISP} fontWeight="700" letterSpacing=".05em" fill="#C9D6E6">COMMITTED</text>
        </g>
      )}

      {/* totals above columns */}
      <text x={x1 + colW / 2} y={toY(tenderTotal) - 10} textAnchor="middle" fontSize={13} fontFamily={MONO} fontWeight="600" fill="#FFFFFF">{fmt(tenderTotal, true)}</text>
      <text x={x2 + colW / 2} y={toY(positionTotal) - 10} textAnchor="middle" fontSize={13} fontFamily={MONO} fontWeight="600" fill="#FFFFFF">{fmt(positionTotal, true)}</text>

      {/* column captions */}
      <text x={x1 + colW / 2} y={H - PB + 20} textAnchor="middle" fontSize={11} fontFamily={DISP} fontWeight="700" letterSpacing=".06em" fill="#C9D6E6">TENDERED VALUE</text>
      <text x={x1 + colW / 2} y={H - PB + 34} textAnchor="middle" fontSize={9} fontFamily={BODY} fill="#7E93AC">Awarded baseline</text>
      <text x={x2 + colW / 2} y={H - PB + 20} textAnchor="middle" fontSize={11} fontFamily={DISP} fontWeight="700" letterSpacing=".06em" fill="#C9D6E6">CURRENT POSITION</text>
      <text x={x2 + colW / 2} y={H - PB + 34} textAnchor="middle" fontSize={9} fontFamily={BODY} fill="#7E93AC">EE + approved VOs + claims</text>

      {/* delta connector */}
      <line x1={x1 + colW} y1={toY(tenderTotal)} x2={x2} y2={toY(positionTotal)} stroke={dCol} strokeWidth={1} strokeDasharray="2 3" opacity={.6} />
      <g transform={`translate(${PL + pw / 2}, ${Math.min(toY(tenderTotal), toY(positionTotal)) - 26})`}>
        <rect x={-58} y={-14} width={116} height={26} rx={13} fill={dCol} />
        <text x={0} y={4} textAnchor="middle" fontSize={11} fontFamily={MONO} fontWeight="700" fill={under ? "#fff" : "#1A0E07"}>
          {under ? "▼ " : "▲ "}{fmt(Math.abs(delta), true)}
        </text>
      </g>
    </svg>
  );
}

// ─── Savings utilisation meter ──────────────────────────────────────────────────
function SavingsMeter({ identified, used, balance }) {
  const usedPct = identified > 0 ? Math.min((used / identified) * 100, 100) : 0;
  return (
    <div>
      <div style={{ position: "relative", height: 26, background: C.sunken, borderRadius: T.radius.full, border: `1px solid ${C.border}`, overflow: "hidden" }}>
        <div className="fd-fill" style={{ "--w": `${usedPct}%`, width: `${usedPct}%`, background: `linear-gradient(90deg,${C.navyMid},${C.navy})` }} />
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 12px", fontSize: 10, fontFamily: DISP, fontWeight: 700, letterSpacing: ".06em", color: usedPct > 14 ? "#fff" : C.inkMuted, textTransform: "uppercase", pointerEvents: "none" }}>
          <span style={{ color: usedPct > 14 ? "#fff" : C.navy }}>Drawn {fmtPct(usedPct)}</span>
          <span style={{ color: usedPct > 86 ? "#fff" : C.inkSubtle }}>{fmtPct(Math.max(0, 100 - usedPct))} retained</span>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 10, color: C.inkSubtle, fontFamily: BODY }}>
        <span>R 0</span>
        <span className="mono">Generated {fmt(identified, true)}</span>
      </div>
    </div>
  );
}

// ─── Donut ─────────────────────────────────────────────────────────────────────
function Donut({ segs, size = 132, centerTop, centerBot }) {
  const R = 44, CX = size / 2, CY = size / 2, circ = 2 * Math.PI * R, total = segs.reduce((s, x) => s + Math.max(0, x.value), 0);
  let off = 0;
  const slices = segs.map((s) => { const pct = total > 0 ? Math.max(0, s.value) / total : 0, dash = pct * circ, gap = circ - dash, sl = { ...s, dash, gap, off }; off += dash; return sl; });
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={CX} cy={CY} r={R} fill="none" stroke={C.sunken} strokeWidth={13} />
      {slices.map((s, i) => (
        <circle key={i} cx={CX} cy={CY} r={R} fill="none" stroke={s.color} strokeWidth={13}
          strokeDasharray={`${s.dash} ${s.gap}`} strokeDashoffset={circ / 4 - s.off} strokeLinecap="butt"
          style={{ transition: "all .5s cubic-bezier(.4,0,.2,1)" }} />
      ))}
      <text x={CX} y={CY - 3} textAnchor="middle" fontSize={13} fontFamily={MONO} fill={C.ink} fontWeight="600">{centerTop}</text>
      <text x={CX} y={CY + 12} textAnchor="middle" fontSize={8.5} fontFamily={DISP} fontWeight="700" letterSpacing=".08em" fill={C.inkSubtle}>{centerBot}</text>
    </svg>
  );
}

// ─── Gauge ─────────────────────────────────────────────────────────────────────
function Gauge({ value, max, color, label, sub }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: C.inkMuted, fontWeight: 500 }}>{label}</span>
        <span className="mono" style={{ fontSize: 12, color, fontWeight: 600 }}>{fmtPct(pct)}</span>
      </div>
      {sub && <div style={{ fontSize: 10, color: C.inkSubtle, marginBottom: 5 }}>{sub}</div>}
      <div className="fd-track">
        <div className="fd-fill" style={{ "--w": `${pct}%`, width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

// ─── Waterfall ─────────────────────────────────────────────────────────────────
function Waterfall({ items }) {
  const W = 700, H = 240, PL = 58, PR = 20, PT = 22, PB = 60, pw = W - PL - PR, ph = H - PT - PB;
  let run = 0;
  const bars = items.map((it) => { const base = it.isTotal ? 0 : run, end = it.isTotal ? it.value : run + it.value; if (!it.isTotal) run = end; return { ...it, base, end }; });
  const allV = bars.flatMap((b) => [b.base, b.end]), minV = Math.min(0, ...allV), maxV = Math.max(...allV), rng = maxV - minV || 1;
  const toY = (v) => PT + ph - ((v - minV) / rng) * ph, bw = Math.max(22, Math.min(54, (pw / bars.length) - 12)), bx = (i) => PL + (i + .5) * (pw / bars.length) - bw / 2;
  const grid = Array.from({ length: 5 }, (_, i) => { const v = minV + (rng / 4) * i; return { v, y: toY(v) }; });
  const barCol = (b) => b.isTotal ? C.navy : b.isSavings ? C.green : b.isContingency ? C.info : b.value >= 0 ? C.navyMid : C.red;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }}>
      {grid.map(({ v, y }, i) => (<g key={i}><line x1={PL} y1={y} x2={W - PR} y2={y} stroke={v === 0 ? C.borderStrong : C.border} strokeWidth={1} /><text x={PL - 6} y={y + 4} textAnchor="end" fontSize={9} fontFamily={MONO} fill={C.inkSubtle}>{fmt(v, true)}</text></g>))}
      {bars.map((b, i) => { if (i === 0 || b.isTotal) return null; const py = toY(bars[i - 1].end); return <line key={`c${i}`} x1={bx(i - 1) + bw} y1={py} x2={bx(i)} y2={py} stroke={C.borderStrong} strokeWidth={1} strokeDasharray="3 2" />; })}
      {bars.map((b, i) => { const y1 = toY(Math.max(b.base, b.end)), y2 = toY(Math.min(b.base, b.end)), bh = Math.max(2, y2 - y1), col = barCol(b), x = bx(i), words = (b.label || "").split("\n"); return (<g key={i}><rect x={x} y={y1} width={bw} height={bh} fill={col} rx={2} /><text x={x + bw / 2} y={y1 - 5} textAnchor="middle" fontSize={9} fontFamily={MONO} fill={C.ink} fontWeight="600">{fmt(b.value, true)}</text>{words.map((w, wi) => (<text key={wi} x={x + bw / 2} y={H - PB + 15 + wi * 11} textAnchor="middle" fontSize={9} fontFamily={BODY} fill={b.isTotal ? C.navy : C.inkMuted} fontWeight={b.isTotal || b.isSavings || b.isContingency ? "700" : "500"}>{w}</text>))}</g>); })}
    </svg>
  );
}

// ─── Dual arc ──────────────────────────────────────────────────────────────────
function DualArc({ spendPct, progPct }) {
  const R1 = 54, R2 = 39, CX = 90, CY = 90, SZ = 180, SA = -Math.PI * .75, SW = Math.PI * 1.5;
  const arc = (r, pct) => { const end = SA + SW * Math.min(pct / 100, 1), x1 = CX + r * Math.cos(SA), y1 = CY + r * Math.sin(SA), x2 = CX + r * Math.cos(end), y2 = CY + r * Math.sin(end), lg = SW * Math.min(pct / 100, 1) > Math.PI ? 1 : 0; return `M ${x1} ${y1} A ${r} ${r} 0 ${lg} 1 ${x2} ${y2}`; };
  const ta = (r) => { const end = SA + SW, x1 = CX + r * Math.cos(SA), y1 = CY + r * Math.sin(SA), x2 = CX + r * Math.cos(end), y2 = CY + r * Math.sin(end); return `M ${x1} ${y1} A ${r} ${r} 0 1 1 ${x2} ${y2}`; };
  const delta = spendPct - progPct, dc = Math.abs(delta) > 5 ? (delta > 0 ? C.amber : C.red) : C.green;
  return (
    <svg width={SZ} height={SZ} viewBox={`0 0 ${SZ} ${SZ}`}>
      <path d={ta(R1)} fill="none" stroke={C.sunken} strokeWidth={9} strokeLinecap="round" />
      <path d={ta(R2)} fill="none" stroke={C.sunken} strokeWidth={9} strokeLinecap="round" />
      <path d={arc(R1, progPct)} fill="none" stroke={C.info} strokeWidth={9} strokeLinecap="round" />
      <path d={arc(R2, spendPct)} fill="none" stroke={C.navy} strokeWidth={9} strokeLinecap="round" />
      <text x={CX} y={CY - 7} textAnchor="middle" fontSize={18} fontFamily={MONO} fill={C.navy} fontWeight="600">{fmtPct(spendPct)}</text>
      <text x={CX} y={CY + 8} textAnchor="middle" fontSize={9} fontFamily={DISP} fontWeight="700" letterSpacing=".06em" fill={C.inkSubtle}>SPEND</text>
      <text x={CX} y={CY + 23} textAnchor="middle" fontSize={12} fontFamily={MONO} fill={dc} fontWeight="600">{delta >= 0 ? "+" : ""}{fmtPct(delta)}</text>
      <circle cx={15} cy={SZ - 20} r={3.5} fill={C.navy} /><text x={24} y={SZ - 16} fontSize={9} fontFamily={BODY} fill={C.inkMuted}>Spend</text>
      <circle cx={15} cy={SZ - 7} r={3.5} fill={C.info} /><text x={24} y={SZ - 3} fontSize={9} fontFamily={BODY} fill={C.inkMuted}>Programme</text>
    </svg>
  );
}

// ─── Variance marker ───────────────────────────────────────────────────────────
function VarianceMarker({ value, label }) {
  const over = value > 0, zero = value === 0, col = zero ? C.green : over ? C.red : C.green, bg = zero ? C.greenSoft : over ? C.redSoft : C.greenSoft, bd = zero ? C.greenBorder : over ? C.redBorder : C.greenBorder, ic = zero ? "●" : over ? "▲" : "▼";
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 12px", borderRadius: T.radius.sm, background: bg, border: `1px solid ${bd}`, fontFamily: MONO, fontSize: 12, color: col, fontWeight: 600 }}>
      <span style={{ fontSize: 9 }}>{ic}</span>
      <span>{fmt(Math.abs(value), true)}</span>
      <span style={{ fontSize: 10, color: C.inkMuted, fontFamily: BODY }}>{label}</span>
    </div>
  );
}

// ─── Section bars ──────────────────────────────────────────────────────────────
function SectionBars({ rows }) {
  if (!rows.length) return <div style={{ color: C.inkSubtle, fontSize: 12, padding: "24px 0", textAlign: "center" }}>No BOQ sections captured yet.</div>;
  const maxV = Math.max(...rows.map((r) => Math.abs(r.revisedForecast) || 1));
  return (
    <div style={{ display: "grid", gap: 10 }}>
      {rows.map((row, i) => {
        const ot = row.varianceToTender > 0, oe = row.varianceToEngineerEstimate > 0, col = ot ? C.red : oe ? C.amber : C.green;
        const fPct = Math.min((row.revisedForecast / maxV) * 100, 100), tPct = Math.min((numberOrZero(row.tenderedAmount) / maxV) * 100, 100);
        return (
          <div key={row.id || i} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: T.radius.md, padding: "11px 14px", borderLeft: `3px solid ${col}`, animation: "fd-rise .3s ease both", animationDelay: `${i * .04}s` }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 7, flexWrap: "wrap", gap: 6 }}>
              <div>
                <span className="mono" style={{ fontSize: 11, color: C.inkSubtle, marginRight: 8 }}>{row.sectionNo || "—"}</span>
                <span style={{ fontSize: 12, color: C.ink, fontWeight: 600 }}>{row.sectionName || "—"}</span>
              </div>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                {ot ? <span className="fd-pill fd-pill-over">▲ {fmt(row.varianceToTender, true)} vs tender</span> : <span className="fd-pill fd-pill-ok">▼ {fmt(Math.abs(row.varianceToTender), true)} saving</span>}
                {oe && <span className="fd-pill fd-pill-warn">▲ {fmt(row.varianceToEngineerEstimate, true)} vs EE</span>}
              </div>
            </div>
            <div style={{ position: "relative", height: 7, background: C.sunken, borderRadius: T.radius.full, border: `1px solid ${C.border}` }}>
              <div style={{ position: "absolute", top: 0, left: 0, height: "100%", borderRadius: T.radius.full, width: `${tPct}%`, background: C.navySoft }} />
              <div style={{ position: "absolute", top: 0, left: 0, height: "100%", borderRadius: T.radius.full, width: `${fPct}%`, background: col, transition: "width .8s ease" }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontSize: 10, color: C.inkSubtle }}>
              <span>Tender: <span className="mono" style={{ color: C.navy }}>{fmt(row.tenderedAmount, true)}</span></span>
              <span>Forecast: <span className="mono" style={{ color: col }}>{fmt(row.revisedForecast, true)}</span></span>
              <span>EE: <span className="mono" style={{ color: C.info }}>{fmt(row.engineersEstimate, true)}</span></span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── KPI card ──────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, accent = C.navy, badge, delay = 0 }) {
  return (
    <div className="fd-stat" style={{ "--accent": accent, animationDelay: `${delay}s` }}>
      <div className="fd-stat-label">{label}</div>
      <div className="fd-stat-value" style={{ color: accent }}><AnimCounter target={numberOrZero(value)} /></div>
      {sub && <div className="fd-stat-sub">{sub}</div>}
      {badge && <div className={`fd-chip ${badge.cls}`}>{badge.text}</div>}
    </div>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────────────
export default function FinancialDashboardSection({
  currentProject, boqRows = [], boqTenderedTotal = 0, boqRevisedTotal = 0,
  variations = [], claims = [], certificates = [], claimsDashboard = {},
  totalClaimCost = 0, soqItems = [], engineersEstimateTotal = 0,
  currentUser,
}) {
  const pid = currentProject?.id;

  // ── Single source of truth for the commercial position ──
  // computeFinanceSummary (src/utils/financeSummary.js) is shared with the mobile
  // Financial tab, so the two screens are guaranteed to show identical figures.
  const fin = useMemo(() => computeFinanceSummary({
    pid, soqItems, boqRows, boqTenderedTotal, variations, claims, certificates, engineersEstimateTotal,
  }), [pid, soqItems, boqRows, boqTenderedTotal, variations, claims, certificates, engineersEstimateTotal]);

  const engineersEstimateTotalComputed = fin.engineersEstimateTotalComputed;

  // ── SOQ Register as the single source of truth ──
  // Tender baseline and per-section figures are derived from the live SOQ items.
  // The BOQ props (boqTenderedTotal / boqRows) are retained ONLY as a fallback
  // for legacy projects whose SOQ register has not been populated yet.
  const hasSoq = Array.isArray(soqItems) && soqItems.length > 0;

  const tenderedBaseline = fin.tenderedBaseline;

  // sectionSource — one row per SOQ section ({ sectionNo, sectionName,
  // tenderedAmount, engineersEstimate, revisedAmount }). revisedAmount is the
  // SOQ-native "revised base" = the engineer's estimate (final qty × rate).
  const sectionSource = useMemo(() => {
    if (hasSoq) {
      return computeSectionBreakdown(soqItems, pid).map((s) => ({
        id: s.id,
        sectionNo: s.sectionNo,
        sectionName: s.sectionName,
        tenderedAmount: s.tenderedAmount,
        engineersEstimate: s.engineersEstimate,
        revisedAmount: s.engineersEstimate,
      }));
    }
    return boqRows;
  }, [hasSoq, soqItems, pid, boqRows]);

  const projVars   = useMemo(() => variations.filter((i) => i.projectId === pid), [variations, pid]);

  // Core commercial figures — all sourced from the shared finance summary so the
  // web dashboard and the mobile Financial tab stay byte-for-byte in agreement.
  const certifiedToDate = fin.certifiedToDate;     // latest cumulative cert subtotal A
  const pendingVoExp    = fin.pendingVoExp;
  const pendingClaimExp = fin.pendingClaimExp;
  const awardedClaims   = fin.awardedClaims;        // committed claim value (pgAwarded)
  const voAdds          = fin.voAdds;               // approved additions (additional + existing-rate)
  const voOmit          = fin.voOmit;               // approved VO omissionItems
  const soqZeroQtySavings = fin.soqZeroQtySavings;  // zero-final-qty SOQ savings (no VO double-count)

  // ── Savings position (EE-vs-BOQ pool model) ──
  // generated = the real saving pool: tendered BOQ less the SOQ engineer's estimate
  //             (i.e. the value released by re-estimating quantities downward).
  // used      = savings drawn down to fund approved VOs (funding source = "Savings"),
  //             which ties to the Variation Order Register funding summary.
  // balance   = net saving retained by the project (pool not yet committed to VOs).
  const savings = useMemo(() => {
    const generated = Math.max(0, tenderedBaseline - engineersEstimateTotalComputed);
    const savingsVOs = projVars
      .filter(isApprovedVariation)
      .filter((v) => String(v.fundingSource || "").trim() === "Savings");
    const used = savingsVOs.reduce((s, v) => s + numberOrZero(v.additionalWorkAmount) + numberOrZero(v.existingRatesAmount), 0);
    const balance = generated - used;
    const usedPct = generated > 0 ? (used / generated) * 100 : 0;
    return { identified: generated, used, balance, usedPct, count: savingsVOs.length };
  }, [tenderedBaseline, engineersEstimateTotalComputed, projVars]);

  // Contingency — project-level rate (default 10%).
  const contingencyRate = useMemo(() => { const r = numberOrZero(currentProject?.contingencyRate); return r > 0 ? r / 100 : 0.10; }, [currentProject?.contingencyRate]);
  const tendContingency = useMemo(() => tenderedBaseline * contingencyRate, [tenderedBaseline, contingencyRate]);
  const contingencyAllocated = useMemo(() =>
    projVars
      .filter(isApprovedVariation)
      .filter((v) => String(v.fundingSource || "").trim() === "Contingency")
      .reduce((s, v) => s + numberOrZero(v.additionalWorkAmount) + numberOrZero(v.existingRatesAmount), 0),
  [projVars]);
  const contingencyBalance  = useMemo(() => tendContingency - contingencyAllocated, [tendContingency, contingencyAllocated]);
  const contingencySpentPct = useMemo(() => tendContingency > 0 ? (contingencyAllocated / tendContingency) * 100 : 0, [contingencyAllocated, tendContingency]);

  const totalTenderExVat = useMemo(() => tenderedBaseline + tendContingency, [tenderedBaseline, tendContingency]);

  // rcv / ffc / rem / spPct — from the shared summary. ffc is RCV plus pending
  // exposures only (contingency-funded additions are already inside rcv via voAdds).
  const rcv = fin.rcv;
  const ffc = fin.ffc;
  const rem = fin.rem;
  const spPct = fin.spPct;
  const vvt = useMemo(() => ffc - totalTenderExVat, [ffc, totalTenderExVat]);
  const vve = useMemo(() => ffc - engineersEstimateTotalComputed, [ffc, engineersEstimateTotalComputed]);
  const delta = useMemo(() => spPct - numberOrZero(currentProject?.progress), [spPct, currentProject?.progress]);

  // ── Current position vs tender (the headline comparison) ──
  // currentPosition = Engineer's Estimate (SOQ-derived) + approved VO additions
  const currentPosition = useMemo(() => engineersEstimateTotalComputed + voAdds, [engineersEstimateTotalComputed, voAdds]);
  const positionVsTender = useMemo(() => currentPosition - totalTenderExVat, [currentPosition, totalTenderExVat]);
  const eeVsBoq = useMemo(() => engineersEstimateTotalComputed - tenderedBaseline, [engineersEstimateTotalComputed, tenderedBaseline]);

  const sectionRows = useMemo(() => sectionSource.map((row) => {
    const tendered = numberOrZero(row.tenderedAmount);
    const ee = numberOrZero(row.engineersEstimate);
    // revisedBase: SOQ-native = the engineer's estimate; BOQ fallback = stored revised amount.
    const revisedBase = numberOrZero(row.revisedAmount);
    const adds = projVars
      .filter(isApprovedVariation)
      .filter((i) => String(i.boqSection || "").trim() === String(row.sectionName || "").trim())
      .reduce((s, i) => s + numberOrZero(i.additionalWorkAmount) + numberOrZero(i.existingRatesAmount), 0);
    const revisedForecast = revisedBase + adds;
    return {
      ...row,
      tenderedAmount: tendered,
      engineersEstimate: ee,
      approvedAdditions: adds,
      revisedBase,
      revisedForecast,
      varianceToTender: revisedForecast - tendered,
      varianceToEngineerEstimate: revisedForecast - ee,
    };
  }), [sectionSource, projVars]);

  const totalDeductions = voOmit + soqZeroQtySavings;
  // Clean bridge: BOQ + VO additions − (omissions + SOQ savings) = Revised Contract;
  // Revised + pending exposures = Forecast Final. Contingency is reported separately
  // (KPIs / summary) rather than as a bridge step, since contingency-funded additions
  // are already inside VO Additions.
  const waterfallItems = [
    { label: "Original\nTender (BOQ)",  value: tenderedBaseline },
    { label: "VO\nAdditions",           value: voAdds },
    { label: "VO Omissions\n& Savings", value: -totalDeductions, isSavings: true },
    { label: "Revised\nContract",       value: rcv, isTotal: true },
    { label: "Pending\nVOs",            value: pendingVoExp },
    { label: "Pending\nClaims",         value: pendingClaimExp },
    { label: "Forecast\nFinal",         value: ffc, isTotal: true },
  ];

  const lateRisk = numberOrZero(claimsDashboard.lateSubmissions) + numberOrZero(claimsDashboard.atRiskClaims);
  const totalExp = pendingVoExp + pendingClaimExp;

  if (!currentProject) return (
    <div className="fd-root" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 320 }}>
      <style>{CSS}</style>
      <div style={{ textAlign: "center", color: C.inkSubtle }}>
        <div style={{ fontSize: 40, marginBottom: 12, color: C.borderStrong }}>◫</div>
        <div style={{ fontFamily: DISP, fontSize: 15, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase" }}>Select a project to view the dashboard</div>
      </div>
    </div>
  );

  return (
    <div className="fd-root">
      <style>{CSS}</style>

      {/* Header */}
      <div style={{ marginBottom: 22, display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="fd-eyebrow" style={{ color: C.accent, marginBottom: 6 }}>Financial Dashboard</div>
          <h2 style={{ margin: 0, fontFamily: DISP, fontSize: 30, fontWeight: 800, letterSpacing: "-.02em", color: C.ink }}>{currentProject.name || currentProject.id}</h2>
          <div style={{ fontSize: 12, color: C.inkMuted, marginTop: 4, fontFamily: MONO }}>{currentProject.id}</div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {positionVsTender !== 0 && <VarianceMarker value={positionVsTender} label={positionVsTender > 0 ? "over tender" : "under tender"} />}
          {vve !== 0 && <VarianceMarker value={vve} label={vve > 0 ? "FFC over EE" : "FFC under EE"} />}
        </div>
      </div>

      {/* KPI row */}
      <div className="fd-stat-grid">
        <KpiCard label="Total Tender (Ex VAT)" value={totalTenderExVat} accent={C.navy}
          sub={`BOQ ${fmt(tenderedBaseline, true)} + contingency ${fmt(tendContingency, true)}`} delay={0} />
        <KpiCard label="Current Position" value={currentPosition} accent={C.navyMid}
          sub="Eng. estimate (SOQ) + approved VOs"
          badge={positionVsTender > 0 ? { cls: "fd-chip-over", text: `▲ ${fmt(positionVsTender, true)} vs tender` } : { cls: "fd-chip-ok", text: `▼ ${fmt(Math.abs(positionVsTender), true)} vs tender` }}
          delay={0.05} />
        <KpiCard label="Approved VO Additions" value={voAdds} accent={C.accent}
          sub={`${projVars.filter(isApprovedVariation).length} approved variation(s)`} delay={0.08} />
        <KpiCard label="Savings Generated" value={savings.identified} accent={C.green}
          sub={`${fmt(savings.used, true)} drawn to fund VOs · ${fmt(savings.balance, true)} retained`}
          badge={savings.balance < 0 ? { cls: "fd-chip-over", text: "▲ Over-drawn" } : { cls: "fd-chip-info", text: `${savings.count} savings-funded VO(s)` }} delay={0.1} />
        <KpiCard label="Forecast Final Cost" value={ffc} accent={ffc > totalTenderExVat ? C.red : C.green}
          sub="Revised contract + pending exposures"
          badge={ffc > totalTenderExVat ? { cls: "fd-chip-over", text: "▲ Over total tender" } : { cls: "fd-chip-ok", text: "✓ Within tender" }} delay={0.15} />
      </div>

      {/* HERO: Tender vs Position (deep navy executive band) */}
      <div className="fd-card" style={{ background: `linear-gradient(160deg, ${C.dark}, ${C.darkAlt})`, border: `1px solid ${C.darkBorder}`, boxShadow: SH.lg, marginBottom: 18, overflow: "hidden" }}>
        <div className="fd-card-pad" style={{ display: "grid", gridTemplateColumns: "minmax(0,1.55fr) minmax(240px,1fr)", gap: 28, alignItems: "center" }}>
          <div>
            <div className="fd-eyebrow" style={{ color: C.accent, marginBottom: 4 }}>Tendered Value · Current Position</div>
            <h3 style={{ margin: "0 0 2px", fontFamily: DISP, fontSize: 18, fontWeight: 700, color: "#fff", letterSpacing: "-.01em" }}>Where the project sits against what was tendered</h3>
            <p style={{ margin: "0 0 8px", fontSize: 12, color: "#9FB2C9", lineHeight: 1.5 }}>Current position is the SOQ engineer&rsquo;s estimate (final quantities &times; tender rates) plus approved variation additions, set against the tendered envelope of BOQ + contingency.</p>
            <ComparisonColumns
              tenderTotal={totalTenderExVat}
              tenderSegs={[
                { label: "Tendered BOQ", value: tenderedBaseline, color: C.navyMid, text: "#fff" },
                { label: "Tendered Contingency", value: tendContingency, color: "#2C5A86", text: "#fff" },
              ]}
              positionTotal={currentPosition + awardedClaims + pendingClaimExp}
              committedTotal={currentPosition + awardedClaims}
              positionSegs={[
                { label: "Engineer's Estimate", value: engineersEstimateTotalComputed, color: "#3F7A5E", text: "#fff" },
                { label: "Approved VO Additions", value: voAdds, color: C.accent, text: "#1A0E07" },
                { label: "Awarded Claims", value: awardedClaims, color: C.info, text: "#fff" },
                { label: "Claims In Review", value: pendingClaimExp, color: C.red, text: "#fff", pending: true },
              ]}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {[
              { k: "Tendered BOQ", v: tenderedBaseline, c: C.navyMid },
              { k: "Tendered Contingency", v: tendContingency, c: "#2C5A86" },
              { k: "Engineer's Estimate (SOQ)", v: engineersEstimateTotalComputed, c: "#5FA37E" },
              { k: "Approved VO Additions", v: voAdds, c: C.accent },
              { k: "Awarded Claims", v: awardedClaims, c: C.info },
              { k: "Claims In Review", v: pendingClaimExp, c: C.red, pending: true },
            ].map((r, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.08)", borderRadius: T.radius.md, borderLeft: `3px solid ${r.c}` }}>
                <span style={{ fontSize: 11, color: "#C9D6E6", fontFamily: DISP, fontWeight: 600, letterSpacing: ".02em" }}>{r.k}{r.pending && <span style={{ fontSize: 9, color: "#F0A99F", marginLeft: 6, fontWeight: 700, letterSpacing: ".06em" }}>PENDING</span>}</span>
                <span className="mono" style={{ fontSize: 13, color: "#fff", fontWeight: 600 }}>{fmt(r.v, true)}</span>
              </div>
            ))}
            <div style={{ marginTop: 2, padding: "12px 14px", background: positionVsTender > 0 ? "rgba(199,59,42,.16)" : "rgba(31,122,77,.16)", border: `1px solid ${positionVsTender > 0 ? "rgba(224,161,153,.4)" : "rgba(168,212,187,.4)"}`, borderRadius: T.radius.md }}>
              <div style={{ fontSize: 10, fontFamily: DISP, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: positionVsTender > 0 ? "#F0A99F" : "#9FD9BA", marginBottom: 4 }}>Position vs Tender Envelope</div>
              <div className="mono" style={{ fontSize: 20, fontWeight: 600, color: "#fff" }}>{positionVsTender > 0 ? "▲ " : "▼ "}{fmt(Math.abs(positionVsTender))}</div>
              <div style={{ fontSize: 11, color: "#9FB2C9", marginTop: 3 }}>EE + approved VOs, excl. claims. EE alone is {eeVsBoq > 0 ? "above" : "below"} tendered BOQ by {fmt(Math.abs(eeVsBoq), true)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* SAVINGS: generated (EE vs BOQ) vs drawn down */}
      <div className="fd-card fd-card-pad" style={{ marginBottom: 18 }}>
        <div className="fd-sectitle" style={{ marginBottom: 16 }}>Savings — Generated vs Drawn Down <span className="rule" /></div>
        <div style={{ display: "grid", gridTemplateColumns: "auto minmax(0,1fr)", gap: 28, alignItems: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            <Donut
              size={146}
              centerTop={fmtPct(Math.min(savings.usedPct, 100))}
              centerBot="DRAWN"
              segs={[
                { value: savings.used, color: C.navy },
                { value: Math.max(0, savings.balance), color: C.green },
              ]}
            />
            <div style={{ fontSize: 11, color: C.inkMuted }}>{savings.count} savings-funded VO(s)</div>
          </div>
          <div>
            <SavingsMeter identified={savings.identified} used={savings.used} balance={savings.balance} />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginTop: 18 }}>
              {[
                { label: "Savings Generated", val: savings.identified, col: C.green, note: "Tendered BOQ less SOQ engineer's estimate" },
                { label: "Drawn to Fund VOs", val: savings.used, col: C.navy, note: "Approved VOs funded from savings" },
                { label: "Retained", val: savings.balance, col: savings.balance < 0 ? C.red : C.accent, note: savings.balance < 0 ? "Over-drawn against generated savings" : "Net saving not yet committed" },
              ].map((t, i) => (
                <div key={i} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: T.radius.md, padding: "12px 14px", borderTop: `3px solid ${t.col}` }}>
                  <div style={{ fontSize: 10, fontFamily: DISP, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: C.inkSubtle, marginBottom: 6 }}>{t.label}</div>
                  <div className="mono" style={{ fontSize: 18, fontWeight: 600, color: t.col }}>{fmt(t.val, true)}</div>
                  <div style={{ fontSize: 10.5, color: C.inkMuted, marginTop: 4, lineHeight: 1.4 }}>{t.note}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Waterfall + Arc */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: 16, marginBottom: 18, alignItems: "stretch" }}>
        <div className="fd-card fd-card-pad">
          <div className="fd-sectitle" style={{ marginBottom: 14 }}>Contract Value Waterfall <span className="rule" /></div>
          <Waterfall items={waterfallItems} />
        </div>
        <div className="fd-card fd-card-pad" style={{ minWidth: 224, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
          <div className="fd-sectitle" style={{ alignSelf: "stretch", marginBottom: 0 }}>Spend vs Programme <span className="rule" /></div>
          <DualArc spendPct={spPct} progPct={numberOrZero(currentProject?.progress)} />
          <div className={`fd-chip ${Math.abs(delta) > 5 ? (delta > 0 ? "fd-chip-warn" : "fd-chip-over") : "fd-chip-ok"}`} style={{ alignSelf: "stretch", justifyContent: "center" }}>
            {Math.abs(delta) > 5 ? (delta > 0 ? "▲ Spend ahead of programme" : "▼ Spend behind programme") : "✓ Broadly aligned"}
          </div>
        </div>
      </div>

      {/* Progress gauges */}
      <div className="fd-card fd-card-pad" style={{ marginBottom: 18 }}>
        <div className="fd-sectitle" style={{ marginBottom: 16 }}>Spend Progress Breakdown <span className="rule" /></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          <div>
            <Gauge value={certifiedToDate} max={rcv} color={C.navy} label="Certified vs Revised Contract" sub={`${fmt(certifiedToDate, true)} of ${fmt(rcv, true)}`} />
            <Gauge value={certifiedToDate} max={ffc} color={C.green} label="Certified vs Forecast Final" />
            <Gauge value={voAdds} max={rcv || 1} color={C.amber} label="VO Additions as % of Contract" />
            <Gauge value={contingencyAllocated} max={tendContingency || 1} color={C.info} label="Contingency Utilisation" sub={`${fmt(contingencyAllocated, true)} of ${fmt(tendContingency, true)}`} />
          </div>
          <div>
            <Gauge value={rem} max={rcv} color={C.info} label="Remaining to Certify" sub={`${fmt(rem, true)} remaining`} />
            <Gauge value={totalExp} max={rcv} color={totalExp > rcv * .1 ? C.red : C.amber} label="Total Pending Exposure" />
            <Gauge value={savings.used} max={savings.identified || 1} color={C.green} label="Savings Drawn Down" sub={`${fmt(savings.used, true)} of ${fmt(savings.identified, true)} generated`} />
            <Gauge value={tenderedBaseline} max={Math.max(ffc, tenderedBaseline)} color={C.navyMid} label="Tender Benchmark" />
          </div>
        </div>
      </div>

      {/* Exposure */}
      <div className="fd-card fd-card-pad" style={{ marginBottom: 18 }}>
        <div className="fd-sectitle" style={{ marginBottom: 16 }}>Variation &amp; Claim Exposure <span className="rule" /></div>
        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 24, alignItems: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <Donut size={132} centerTop={fmt(totalExp, true)} centerBot="EXPOSURE"
              segs={[{ value: pendingVoExp, color: C.amber }, { value: pendingClaimExp, color: C.red }, { value: Math.max(0, rcv - totalExp), color: C.border }]} />
            {totalExp > 0 && rcv > 0 && <div className={`fd-chip ${totalExp > rcv * .1 ? "fd-chip-over" : "fd-chip-warn"}`}>{fmtPct((totalExp / rcv) * 100)} of contract</div>}
            {totalExp === 0 && <div className="fd-chip fd-chip-ok">✓ No exposure</div>}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {[
              { label: "Pending VOs",          val: projVars.filter((i) => !["Approved", "Rejected"].includes(String(i.status || ""))).length, col: C.amber, suf: " items" },
              { label: "Pending VO Value",     val: pendingVoExp,   col: C.amber, money: true },
              { label: "Claims In Review",     val: numberOrZero(claimsDashboard.inReviewClaims), col: C.red, suf: " claims" },
              { label: "Pending Claim Value",  val: pendingClaimExp, col: C.red, money: true },
              { label: "Late / Time-Bar Risk", val: lateRisk, col: lateRisk > 0 ? C.red : C.green, suf: " claims", badge: lateRisk > 0 ? { cls: "fd-chip-over", text: "⚠ Attention" } : { cls: "fd-chip-ok", text: "✓ Clear" } },
            ].map((it, i) => (
              <div key={i} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: T.radius.md, padding: "11px 14px", borderLeft: `3px solid ${it.col}` }}>
                <div style={{ fontSize: 10, color: C.inkSubtle, fontFamily: DISP, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 5 }}>{it.label}</div>
                <div className="mono" style={{ fontSize: 16, color: it.col, fontWeight: 600 }}>{it.money ? fmt(it.val, true) : `${it.val}${it.suf || ""}`}</div>
                {it.badge && <div className={`fd-chip ${it.badge.cls}`}>{it.badge.text}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* BOQ sections */}
      <div className="fd-card fd-card-pad" style={{ marginBottom: 18 }}>
        <div className="fd-sectitle" style={{ marginBottom: 14 }}>BOQ Section Performance <span className="rule" /></div>
        {sectionRows.length > 0 && (
          <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
            <span className="fd-pill fd-pill-over">▲ Overrun vs tender</span>
            <span className="fd-pill fd-pill-ok">▼ Saving vs tender</span>
            <span className="fd-pill fd-pill-warn">▲ Over Engineer&rsquo;s Estimate</span>
            <span className="fd-pill fd-pill-info">Navy track = tender · Bar = forecast</span>
          </div>
        )}
        <SectionBars rows={sectionRows} />
      </div>

      {/* Summary table */}
      <div className="fd-card fd-card-pad">
        <div className="fd-sectitle" style={{ marginBottom: 14 }}>Commercial Summary <span className="rule" /></div>
        <div style={{ overflowX: "auto" }}>
          <table className="fd-table">
            <thead><tr><th>Item</th><th className="r">Amount</th><th>Notes</th></tr></thead>
            <tbody>
              {[
                { label: "Original Tendered BOQ",          val: tenderedBaseline,              note: hasSoq ? "Tendered schedule from SOQ Register (ex contingency)" : "Tendered BOQ (ex contingency)" },
                { label: "Tendered Contingency",           val: tendContingency,               note: "Contingency allowance in tender", contingency: true },
                { label: "Total Tender Value (Ex VAT)",    val: totalTenderExVat,              note: "BOQ + contingency", bold: true },
                { label: "Engineer's Estimate Total",      val: engineersEstimateTotalComputed, note: soqItems.length > 0 ? "Derived from SOQ final estimated qty × tender rates" : "Current EE summary" },
                { label: "Approved VO Additions",          val: voAdds,                        note: "Additional work + existing-rate work, all approved VOs (ties to VO Register)" },
                { label: "Current Position (EE + VOs)",    val: currentPosition,               note: "Engineer's estimate plus approved VO additions", bold: true },
                { label: "VO Omissions & SOQ Savings",     val: totalDeductions,               note: "Combined deductions from contract value", savings: true },
                { label: "  ↳ Approved VO Omissions",      val: voOmit,                        note: "Live from approved VO omissionItems", sub: true },
                { label: "  ↳ SOQ Savings (Zero Final Qty)", val: soqZeroQtySavings,           note: `${(soqItems || []).filter((i) => i.projectId === pid && (i.finalEstimatedQty === 0 || i.finalEstimatedQty === "0" || i.finalEstimatedQty === "" || i.finalEstimatedQty === null || i.finalEstimatedQty === undefined) && numberOrZero(i.tenderQty) > 0).length} SOQ items with zero final estimated qty`, sub: true },
                { label: "Savings Generated (EE vs BOQ)",  val: savings.identified,            note: `Tendered BOQ less engineer's estimate · ${fmt(savings.used, true)} drawn to fund VOs`, savings: true },
                { label: "Revised Contract Value",         val: rcv,                           note: "Tender + VO Additions − VO Omissions − SOQ Savings", bold: true },
                { label: "Contingency Allocated to VOs",   val: contingencyAllocated,          note: "Gross value of approved Contingency-funded VOs", contingency: true },
                { label: "Contingency Balance Remaining",  val: contingencyBalance,            note: "Tendered contingency less allocated", contingencyBal: true },
                { label: "Certified to Date",              val: certifiedToDate,               note: "Latest certificate subtotal A" },
                { label: "Remaining to Certify",           val: rem,                           note: "Revised contract less certified" },
                { label: "Pending VO Exposure",            val: pendingVoExp,                  note: "Unapproved variation exposure" },
                { label: "Pending Claim Exposure",         val: pendingClaimExp,               note: "Claims currently in review" },
                { label: "Forecast Final Cost",            val: ffc,                           note: "Revised contract + pending VO & claim exposures", bold: true },
                { label: "Variance vs Total Tender",       val: vvt,                           note: vvt > 0 ? "Over total tender (BOQ + contingency)" : "Within total tender", variance: true },
                { label: "Variance vs Eng. Estimate",      val: vve,                           note: vve > 0 ? "Above engineer's estimate" : "Within EE", variance: true },
              ].map((row, i) => (
                <tr key={i} style={{ opacity: row.sub ? 0.72 : 1 }}>
                  <td style={{ fontWeight: row.bold ? 700 : 500, color: row.bold ? C.ink : row.sub ? C.inkSubtle : C.inkMuted, fontStyle: row.sub ? "italic" : "normal", paddingLeft: row.sub ? 28 : 12 }}>{row.label}</td>
                  <td className="r" style={{
                    color: row.variance ? (row.val > 0 ? C.red : C.green)
                      : row.savings ? C.green
                      : row.contingencyBal ? (row.val < 0 ? C.red : C.info)
                      : row.contingency ? C.info
                      : row.sub ? C.inkSubtle
                      : row.bold ? C.navy
                      : C.ink,
                    fontWeight: row.bold || row.variance || row.savings || row.contingency || row.contingencyBal ? 700 : 500,
                  }}>
                    {row.variance && row.val > 0 && <span style={{ marginRight: 4 }}>▲</span>}
                    {row.variance && row.val < 0 && <span style={{ marginRight: 4 }}>▼</span>}
                    {row.savings && !row.sub && <span style={{ marginRight: 4 }}>▼</span>}
                    {row.contingencyBal && row.val < 0 && <span style={{ marginRight: 4 }}>▲</span>}
                    {row.contingencyBal && row.val >= 0 && <span style={{ marginRight: 4 }}>▼</span>}
                    {fmt(Math.abs(row.val))}
                  </td>
                  <td style={{ fontSize: 11, color: C.inkSubtle }}>{row.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}